import React, { useState, useEffect, useRef } from 'react';
import { Search, ArrowLeft, X, Loader2, AlertCircle, ArrowRight, Film, Trophy, Newspaper, User } from 'lucide-react';
import { searchPlayers, searchNews } from '../../services/footballService';
import { useDebounce } from '../../hooks/useDebounce';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../i18n';

interface SearchOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    onNavigate: (data: any) => void; 
    onSearch?: (query: string) => void; 
    type?: 'player' | 'news' | 'game' | 'shorts';
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({ isOpen, onClose, onNavigate, onSearch, type = 'player' }) => {
    const { lang } = useLanguage();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    
    const debouncedQuery = useDebounce(query, 400);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setQuery('');
            setResults([]);
        }
    }, [isOpen]);

    useEffect(() => {
        if (type === 'shorts') return;
        if (debouncedQuery.length >= (type === 'player' ? 3 : 2)) {
            setIsSearching(true);
            const performSearch = async () => {
                let res: any[] = [];
                try {
                    if (type === 'player') res = await searchPlayers(debouncedQuery);
                    else if (type === 'news') res = await searchNews(debouncedQuery, lang);
                } catch (e) { console.error(e); }
                setResults(res);
                setIsSearching(false);
            };
            performSearch();
        } else {
            setResults([]);
            setIsSearching(false);
        }
    }, [debouncedQuery, type, lang]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && query.length > 1) {
            if (onSearch) {
                onSearch(query);
                onClose();
            }
        }
        if (e.key === 'Escape') onClose();
    };

    if (!isOpen) return null;

    const getPlaceholder = () => {
        if (type === 'news') return t(lang, 'searchNewsPlaceholder');
        if (type === 'game') return t(lang, 'searchGamePlaceholder');
        if (type === 'shorts') return t(lang, 'searchShortsPlaceholder');
        return t(lang, 'searchPlayerPlaceholder');
    };

    const renderResultItem = (item: any) => {
        if (type === 'player') {
            return (
                <div className="w-full flex items-center gap-4 py-4 px-2 md:hover:bg-slate-50 text-left group rounded-2xl transition-colors">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 shrink-0">
                        <img 
                            src={item.avatar} 
                            alt={item.name} 
                            className="w-full h-full object-cover" 
                            onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?background=f1f5f9&color=94a3b8&bold=true&name=${encodeURIComponent(item.name)}`; }} 
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="font-black text-slate-900 text-[15px] md:group-hover:text-rose-600 truncate uppercase tracking-tight">
                            {item.name}
                        </h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.1em] truncate mt-0.5">
                            {item.team}
                        </p>
                    </div>
                    <div className="text-slate-200 md:group-hover:text-rose-400 shrink-0 pr-2">
                        <ArrowRight size={18} strokeWidth={3} />
                    </div>
                </div>
            );
        } else if (type === 'news') {
             return (
                <div className="w-full flex items-start gap-4 py-4 px-2 md:hover:bg-slate-50 text-left group rounded-2xl transition-colors">
                    <div className="w-20 h-14 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                         <img 
                            src={item.image} 
                            alt={item.title} 
                            className="w-full h-full object-cover opacity-90 md:group-hover:opacity-100" 
                            onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1522778119026-d647f0565c6a?auto=format&fit=crop&w=1200&q=80'; }} 
                        />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="font-black text-slate-900 text-sm md:group-hover:text-rose-600 line-clamp-2 leading-snug uppercase tracking-tight">
                            {item.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[9px] text-rose-500 font-black uppercase tracking-widest">{item.source || 'INTEL'}</span>
                            <span className="text-slate-300 font-bold text-[9px] uppercase tracking-tighter">• {item.time}</span>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="fixed inset-0 z-[150] flex md:left-[256px]">
            {/* Flat Backdrop for mobile */}
            <div className="absolute inset-0 bg-white md:bg-white/95 md:backdrop-blur-xl" onClick={onClose}></div>
            
            {/* Main Surface - Simplified & Flat */}
            <div className="relative w-full h-full bg-white flex flex-col shadow-none">
                {/* Header */}
                <div className="sticky top-0 z-20 bg-white">
                    <div className="max-w-4xl mx-auto w-full px-4 md:px-8 pt-2 md:py-6 flex items-center gap-3 md:gap-4">
                        <button 
                            onClick={onClose} 
                            className="w-10 h-10 flex items-center justify-center text-slate-400 md:hover:text-slate-900 active:scale-90 transition-all"
                        >
                            {/* Fix: removed md:size prop which is not supported by Lucide icons */}
                            <ArrowLeft className="w-[22px] h-[22px] md:w-6 md:h-6" strokeWidth={2.5} />
                        </button>
                        
                        <div className="flex-1 relative group">
                            {/* Fix: removed md:size prop and used responsive Tailwind classes instead */}
                            <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-rose-500 w-[18px] h-[18px] md:w-5 md:h-5" strokeWidth={3} />
                            <input 
                                ref={inputRef}
                                type="text" 
                                placeholder={getPlaceholder()}
                                className="w-full bg-transparent text-slate-900 rounded-none py-3 md:py-4 pl-8 md:pl-10 pr-8 md:pr-10 focus:outline-none font-black text-lg md:text-xl placeholder:text-slate-200"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            {query && (
                                <button 
                                    onClick={() => setQuery('')} 
                                    className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center bg-slate-50 text-slate-400 md:hover:text-rose-500 rounded-full transition-colors"
                                >
                                    {/* Fix: removed md:size prop which is not supported by Lucide icons */}
                                    <X className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={3} />
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Bottom Hairline - Flat & Simple */}
                    <div className="h-[1px] bg-slate-100 mx-4 md:mx-8"></div>
                </div>

                {/* Results Area */}
                <div className="flex-1 overflow-y-auto custom-scrollbar px-4 md:px-12 pb-24">
                    <div className="max-w-4xl mx-auto w-full">
                        {isSearching ? (
                            <div className="flex flex-col items-center justify-center py-20 md:py-40 gap-4">
                                <Loader2 className="w-8 h-8 md:w-10 md:h-10 animate-spin text-rose-500" strokeWidth={3} />
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{t(lang, 'consultingGrid')}</p>
                            </div>
                        ) : results.length > 0 ? (
                            <div className="space-y-1 pt-4 md:pt-6">
                                <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em] mb-3 md:mb-4 px-2">{t(lang, 'matchesFound')} ({results.length})</p>
                                {results.map((item, idx) => (
                                    <button
                                        key={item.id || idx}
                                        onClick={() => { 
                                            onNavigate(type === 'player' ? item.id : item); 
                                            onClose(); 
                                        }}
                                        className="w-full block"
                                    >
                                        {renderResultItem(item)}
                                    </button>
                                ))}
                            </div>
                        ) : query.length >= (type === 'player' ? 3 : 2) ? (
                            <div className="text-center py-20 md:py-40">
                                <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-200 mx-auto mb-6">
                                    {/* Fix: removed md:size prop which is not supported by Lucide icons */}
                                    <AlertCircle className="w-7 h-7 md:w-8 md:h-8" />
                                </div>
                                <h3 className="text-slate-900 font-black text-lg md:text-xl mb-1 uppercase tracking-tight">{t(lang, 'zeroIntel')}</h3>
                                <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400">{t(lang, 'refineSearch')}</p>
                            </div>
                        ) : null /* Initial empty state - all waiting messages removed */}
                    </div>
                </div>
            </div>
        </div>
    );
};
